import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@/elements/Button";
import { withOpeningReset } from "@/utils/withOpeningReset";
import {
  BonificaDto,
  useUpdateBonifiche,
  BonificaImmagineDtoInt32EntityChange,
  NodoDto,
  getGetNodoQueryKey,
  getGetNodoPortaleQueryKey,
} from "@/api";
import { Controller, useForm } from "react-hook-form";
import { FormHelperText, InputLabel } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SmallLoading } from "../Loading";
import { useQueryClient } from "@tanstack/react-query";
import { DateTimeField } from "@mui/x-date-pickers";
import moment from "moment/moment";
import DescrizioneBonificaPicker from "@/components/DescrizioneBonificaPicker";
import FileField from "@/components/FileField";
import BonificaImmaginiDialog from "../BonificaImmaginiDialog";
import { portaleConfig } from "@/portaliConfig";

function BonificaDialog({
  open = false,
  onClose,
  nodo,
  bonifica,
}: {
  open?: boolean;
  onClose?: () => void;
  nodo: NodoDto;
  bonifica?: Partial<BonificaDto> | null;
}) {
  const { t } = useTranslation();
  const handleClose = () => {
    onClose?.();
  };

  const {
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    resetField,
    control,
  } = useForm<BonificaDto>({
    defaultValues: bonifica || {},
  });

  const { mutateAsync, isLoading } = useUpdateBonifiche();

  const queryClient = useQueryClient();
  const onSubmit = async (data: BonificaDto) => {
    try {
      const changesImmagini: BonificaImmagineDtoInt32EntityChange[] = [];
      let i = 0;
      let j = 0;
      const immaginiIniziali = bonifica?.bonificaImmagini ?? [];
      const immaginiAggiornate = data?.bonificaImmagini ?? [];
      while (i < immaginiIniziali.length && j < immaginiAggiornate.length) {
        if (
          immaginiIniziali[i].uploadedFileId !==
          immaginiAggiornate[i].uploadedFileId
        ) {
          changesImmagini.push({
            id: immaginiIniziali[i].id!,
            changeType: "updated",
            entity: {
              uploadedFileId: immaginiAggiornate[i].uploadedFileId,
            },
          });
        }
        i++;
        j++;
      }
      while (i < immaginiIniziali.length) {
        changesImmagini.push({
          id: immaginiIniziali[i].id!,
          changeType: "deleted",
          entity: {
            uploadedFileId: immaginiIniziali[i].uploadedFileId,
          },
        });
        i++;
      }
      while (j < immaginiAggiornate.length) {
        changesImmagini.push({
          changeType: "added",
          entity: {
            uploadedFileId: immaginiAggiornate[j].uploadedFileId,
          },
        });
        j++;
      }
      data.bonificaImmagini = changesImmagini;

      await mutateAsync({
        id: nodo.id,
        data: [
          {
            id: bonifica?.id,
            changeType: bonifica?.id ? "updated" : "added",
            entity: data,
          },
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id),
      });
      onClose?.();
    } catch (err) {}
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {bonifica?.id
          ? t("bonificaDialog.titolo.modifica")
          : t("bonificaDialog.titolo.crea")}
      </DialogTitle>
      <DialogContent>
        <InputLabel htmlFor="crea-bonifica-data">
          {t("bonificaDialog.campi.data")}
        </InputLabel>
        <Controller
          name="data"
          control={control}
          rules={{ required: t("bonificaDialog.errori.data") }}
          render={({ field, fieldState: { error } }) => {
            const { value, name, onBlur, onChange, ref } = field;
            const m =
              typeof value === "string"
                ? moment(value)
                : moment.isMoment(value)
                ? value
                : null;
            return (
              <>
                <DateTimeField
                  ref={ref}
                  format="DD/MM/YYYY"
                  name={name}
                  onBlur={onBlur}
                  onChange={(value) => onChange(value?.format("YYYY-MM-DD"))}
                  value={m}
                  fullWidth
                />
                {error && (
                  <FormHelperText error>{error?.message}</FormHelperText>
                )}
              </>
            );
          }}
        />

        <InputLabel htmlFor="crea-bonifica-scadenza">
          {t("bonificaDialog.campi.scadenza")}
        </InputLabel>
        <Controller
          name="scadenza"
          control={control}
          render={({ field, fieldState: { error } }) => {
            const { value, name, onBlur, onChange, ref } = field;
            const m =
              typeof value === "string"
                ? moment(value)
                : moment.isMoment(value)
                ? value
                : null;
            return (
              <>
                <DateTimeField
                  ref={ref}
                  format="DD/MM/YYYY"
                  name={name}
                  onBlur={onBlur}
                  onChange={(value) =>
                    onChange(value?.startOf("day")?.format("YYYY-MM-DD"))
                  }
                  value={m}
                  fullWidth
                />
                {error && (
                  <FormHelperText error>{error?.message}</FormHelperText>
                )}
              </>
            );
          }}
        />

        <InputLabel htmlFor="crea-bonifica-descrizione">
          {t("bonificaDialog.campi.descrizione")}
        </InputLabel>
        <Controller
          name="descrizione"
          control={control}
          rules={{ required: t("bonificaDialog.errori.descrizione") }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <DescrizioneBonificaPicker
                value={value}
                onChange={(e, v) => onChange(v)}
              />
              {error && <FormHelperText error>{error?.message}</FormHelperText>}
            </>
          )}
        />

        <InputLabel htmlFor="crea-bonifica-immagini">
          {t("bonificaDialog.campi.immagini")}
        </InputLabel>
        <Controller
          name="bonificaImmagini"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <>
                <BonificaImmaginiDialog
                  value={value}
                  onChange={(v) => onChange(v)}
                />
              </>
            );
          }}
        />

        <InputLabel htmlFor="crea-bonifica-rapportino">
          {t("bonificaDialog.campi.rapportino")}
        </InputLabel>
        <Controller
          name="fileRapportino"
          control={control}
          rules={{ required: t("bonificaDialog.errori.file") }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <FileField
                value={value}
                onChange={(f) => {
                  onChange(f);
                  if (f?.id) {
                    setValue("fileRapportinoId", f?.id);
                  } else {
                    resetField("fileRapportinoId");
                  }
                }}
              />
              {error && <FormHelperText error>{error?.message}</FormHelperText>}
            </>
          )}
        />
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isValid || isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {bonifica?.id
            ? t("bonificaDialog.azioni.salva")
            : t("bonificaDialog.azioni.crea")}
        </Button>
        <Button
          variant="contained"
          color="light"
          disabled={isLoading}
          onClick={handleClose}
        >
          {t("azioni.annulla")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(BonificaDialog);
